// Color system for the social graph. Everything WebGL-bound goes through the
// premultiply helpers: sigma blends with (ONE, ONE_MINUS_SRC_ALPHA) but its shaders
// don't premultiply RGB, so raw translucent colors accumulate additively and bleach
// dense areas to white.
import { EdgeAttributes, GraphColorMode, GraphHighlight, NodeAttributes, ScopeFlow, ZScoreBucket } from './graphTypes';

// Dusty, desaturated hues (Cosmograph-style) — saturated neon reads harsh on the
// slate canvas; these keep hue separation while staying soft
export const COMMUNITY_PALETTE = [
  '#7B7FD4', // periwinkle
  '#E06C9F', // dusty pink
  '#A78BFA', // soft violet
  '#7BC49A', // sage
  '#E3C567', // sand
  '#7FA6E0', // soft blue
  '#E09A66', // muted orange
  '#D98BB6', // rose
  '#6FBFAD', // seafoam
  '#B49BE0', // lavender
];

// External (out-of-scope) actors stay distinguishable from in-scope ones but keep
// most of their hue — 30% ('4D') read as near-invisible grey orbits on real data
export const EXTERNAL_ALPHA = '99';
export const EDGE_ALPHA = 'B3';
export const RECIPROCAL_EDGE_ALPHA = 'FF';
// Community-colored edges are denser visually (many share a hue), so they stay more transparent
export const COMMUNITY_EDGE_ALPHA = '59';
export const COMMUNITY_RECIPROCAL_EDGE_ALPHA = 'B3';
// Long-range edges between communities overlap heavily in the center — keep them
// fainter than intra-community ones so they read as background context
export const INTER_COMMUNITY_EDGE_ALPHA = '33';
export const INTER_COMMUNITY_RECIPROCAL_EDGE_ALPHA = '80';

// Overlapping edge bundles converge to rgb/alpha (the blend's fixed point), and where
// many pastel hues stack (hubs) that average is near-white — damp the ceiling so
// bundles converge to a clearly-hued color instead
export const EDGE_BUNDLE_DAMPING = 0.8;

// Sigma's WebGL shaders don't premultiply RGB by alpha, so translucent elements
// deposit full-strength RGB and accumulate additively — overlapping edges bleach to
// white at hubs, and translucent (external) nodes over-brighten whatever is beneath.
// Premultiplying the RGB ourselves fixes that: each pass then contributes rgb*a and
// the blend's fixed point is the intended hue instead of white.
export function premultiplyColor(hex6: string, alphaHex: string, damping = 1): string {
  const a = (parseInt(alphaHex, 16) / 255) * damping;
  const rgb = [0, 1, 2]
    .map((i) => {
      const channel = parseInt(hex6.slice(1 + i * 2, 3 + i * 2), 16);
      return Math.round(channel * a)
        .toString(16)
        .padStart(2, '0');
    })
    .join('');
  return `#${rgb}${alphaHex}`;
}

export function premultiplyEdgeColor(hex6: string, alphaHex: string): string {
  return premultiplyColor(hex6, alphaHex, EDGE_BUNDLE_DAMPING);
}

// "Thickness by interactions" edges are up to ~3× wider than classic ones, and at
// the resting alphas (tuned for hairline edges) the wide bands read as washed-out
// smears. In that mode every edge alpha moves this fraction of the way toward
// fully opaque (0 = untouched, 1 = solid): the remaining transparency shrinks by
// 70%, so the relative ordering (mention > community > inter-community,
// reciprocal > plain) survives instead of collapsing at the opaque ceiling the
// way a plain multiplier would. First pass was a ×1.4 multiplier — too faint.
export const VOLUME_EDGE_OPACITY_LIFT = 0.7;

// Top-volume edges (see TOP_VOLUME_EDGE_COUNT) must be the brightest lines on
// the canvas while still reading as "their" community/platform/mention color:
// the hue is pushed this fraction toward white and rendered fully opaque with NO
// bundle damping — RGB is the only channel that survives premultiply overlaps,
// so this is what makes them pop inside a dense hub, not alpha.
export const TOP_VOLUME_EDGE_LIFT = 0.3;

export function mixTowardWhite(hex6: string, t: number): string {
  const mixed = [0, 1, 2].map((i) => {
    const channel = parseInt(hex6.slice(1 + i * 2, 3 + i * 2), 16);
    return Math.round(channel + (255 - channel) * t)
      .toString(16)
      .padStart(2, '0');
  });
  return `#${mixed.join('')}`;
}

export function topVolumeEdgeColor(hex6: string): string {
  return `${mixTowardWhite(hex6, TOP_VOLUME_EDGE_LIFT)}FF`;
}

export function liftAlpha(alphaHex: string, fraction: number): string {
  const alpha = parseInt(alphaHex, 16);
  const lifted = Math.min(255, Math.round(alpha + (255 - alpha) * fraction));
  return lifted.toString(16).padStart(2, '0').toUpperCase();
}

// How far cross-cluster edge hues are mixed toward the background in "dimmed" mode.
// Alpha alone can't tame a hairball: stacked translucent edges converge to rgb/alpha
// regardless of how low alpha is (it only slows convergence), so when thousands of
// inter-community edges overlap they still fill the space at full hue strength.
// Dimming the RGB ceiling itself is what keeps the gap between clusters dark.
export const INTER_CLUSTER_MUTE = 0.55;

export type InterClusterMode = 'dimmed' | 'hidden' | 'full';

// Defaults shared by the initial-color builders (buildGraph) and the page's
// colorMode/interClusterMode useState, so the two can't silently drift apart.
export const DEFAULT_COLOR_MODE: GraphColorMode = 'community';
export const DEFAULT_INTER_CLUSTER_MODE: InterClusterMode = 'dimmed';

// Single hue for monochrome mode — soft blue from the community palette
export const MONO_BASE = '#7FA6E0';

// Scope color scheme: nodes are colored binary by whether the actor is inside the
// analysis scope, edges by flow direction across the scope boundary. Inbound/outbound
// use vivid cyan vs vivid orange — a colorblind-safe pair at maximum hue distance;
// the earlier muted blue (#7FA6E0) sank into the slate background and the whole
// graph read as one orange mass. Internal traffic shares one calm hue (dense, like
// community mode), and external↔external chatter stays background gray.
export const SCOPE_IN_NODE_COLOR = '#7BC49A'; // sage — actor in scope
export const SCOPE_OUT_NODE_COLOR = '#7C8597'; // gray — external actor
export const SCOPE_INBOUND_COLOR = '#3FC9E8'; // vivid cyan — external → in-scope
export const SCOPE_OUTBOUND_COLOR = '#F07A3D'; // vivid orange — in-scope → external
// cross-boundary edges are the signal of the connection scheme — brighter than
// the standard edge alpha so both directions survive premultiply damping in
// dense fans
export const SCOPE_CROSS_EDGE_ALPHA = 'CC';
export const SCOPE_EXTERNAL_EDGE_ALPHA = '40';
export const SCOPE_EXTERNAL_RECIPROCAL_EDGE_ALPHA = '80';

export const SCOPE_EDGE_STYLES: Record<ScopeFlow, { base: string; alpha: string; reciprocalAlpha: string }> = {
  internal: { base: SCOPE_IN_NODE_COLOR, alpha: COMMUNITY_EDGE_ALPHA, reciprocalAlpha: COMMUNITY_RECIPROCAL_EDGE_ALPHA },
  inbound: { base: SCOPE_INBOUND_COLOR, alpha: SCOPE_CROSS_EDGE_ALPHA, reciprocalAlpha: RECIPROCAL_EDGE_ALPHA },
  outbound: { base: SCOPE_OUTBOUND_COLOR, alpha: SCOPE_CROSS_EDGE_ALPHA, reciprocalAlpha: RECIPROCAL_EDGE_ALPHA },
  external: { base: SCOPE_OUT_NODE_COLOR, alpha: SCOPE_EXTERNAL_EDGE_ALPHA, reciprocalAlpha: SCOPE_EXTERNAL_RECIPROCAL_EDGE_ALPHA },
};

// Connection color scheme (PM colours 2026-10-06): three edge kinds relative to
// the actor group, every node neutral gray —
//   in-group ↔ in-group        blue   CONNECTION_INTERNAL_COLOR
//   external → in-group        sage   CONNECTION_INBOUND_COLOR  ("Internal connection" row)
//   in-group → external        gray   CONNECTION_OUTBOUND_COLOR ("External connection" row)
// and external ↔ external chatter stays the faint background gray with no
// legend row. The three coloured kinds share the bright cross-edge alpha, skip
// the cross-cluster mute and draw above the background bundle (edge zIndex in
// the page's recolor effect): the in-group set is small on a mixed graph
// (Niedersachsen: 392 in / 1,608 out) and vanished at base weight — tried on
// 2026-10-06, reverted the same day. An all-in-group graph is therefore one
// solid blue mass (Ukrainian Media Top Sources: 41,690 in-group edges) —
// accepted trade-off, the mixed graph is the scheme's purpose.
export const CONNECTION_INTERNAL_COLOR = '#2261E9';
export const CONNECTION_INBOUND_COLOR = '#7BC49A';
export const CONNECTION_OUTBOUND_COLOR = '#7C8597';

export const CONNECTION_EDGE_STYLES: Record<ScopeFlow, { base: string; alpha: string; reciprocalAlpha: string }> = {
  internal: { base: CONNECTION_INTERNAL_COLOR, alpha: SCOPE_CROSS_EDGE_ALPHA, reciprocalAlpha: RECIPROCAL_EDGE_ALPHA },
  inbound: { base: CONNECTION_INBOUND_COLOR, alpha: SCOPE_CROSS_EDGE_ALPHA, reciprocalAlpha: RECIPROCAL_EDGE_ALPHA },
  outbound: { base: CONNECTION_OUTBOUND_COLOR, alpha: SCOPE_CROSS_EDGE_ALPHA, reciprocalAlpha: RECIPROCAL_EDGE_ALPHA },
  external: { base: SCOPE_OUT_NODE_COLOR, alpha: SCOPE_EXTERNAL_EDGE_ALPHA, reciprocalAlpha: SCOPE_EXTERNAL_RECIPROCAL_EDGE_ALPHA },
};

// the edge kinds the connection scheme surfaces (everything but out ↔ out)
export function isConnectionSurfacedFlow(flow: ScopeFlow): boolean {
  return flow !== 'external';
}

// Platform scheme palette (PM 2026-10-16). A platform's hue is its RANK on the
// current graph — the platform with the most actors gets slot 1, the next slot
// 2, and so on, wrapping after 14 — not a fixed per-platform colour, so the
// same platform can differ between graphs; the legend always names it. Ranks
// are computed once per build on the full graph (rankPlatformColors in
// buildGraph.ts) and stored on nodes/edges as `platformBase`, so filters never
// re-rank and the recolour effect stays a plain attribute read.
export const PLATFORM_PALETTE = [
  '#4269D0',
  '#A463F2',
  '#3CA951',
  '#FF725C',
  '#EFB118',
  '#6CC5B0',
  '#FF8AB7',
  '#0A2076',
  '#51028B',
  '#2A4B00',
  '#961D0E',
  '#866100',
  '#076D66',
  '#9A3A63',
];
export const platformColorForRank = (rank: number): string => PLATFORM_PALETTE[rank % PLATFORM_PALETTE.length];

// Z-score anomaly scale for edges in z-score analysis mode: the hue encodes how
// statistically anomalous the interaction intensity is (per FDE spec), replacing the
// structural community/mention schemes. Anomalous buckets get high alphas so they
// pop; the normal bucket stays faint so the bulk of the graph reads as background.
export const ZSCORE_HIGH_THRESHOLD = 2.0;
export const ZSCORE_ELEVATED_THRESHOLD = 1.0;
export const ZSCORE_HIGH_COLOR = '#E25B5B'; // Z > 2: suspected coordination / automation
export const ZSCORE_ELEVATED_COLOR = '#E09A66'; // 1 < Z ≤ 2: needs analyst review
export const ZSCORE_NORMAL_COLOR = '#7C8597'; // 0 ≤ Z ≤ 1: within statistical norm
export const ZSCORE_LOW_COLOR = '#7FA6E0'; // Z < 0: sporadic or fading interaction
export const ZSCORE_HIGH_ALPHA = 'FF';
export const ZSCORE_ELEVATED_ALPHA = 'CC';
export const ZSCORE_NORMAL_ALPHA = '40';
export const ZSCORE_LOW_ALPHA = '73';

// Node-level bucketing for the Actors tab / spotlight — same thresholds as edges,
// applied to a node's avg z-score
export function zscoreNodeBucket(z: number): ZScoreBucket {
  if (z > ZSCORE_HIGH_THRESHOLD) return 'high';
  if (z > ZSCORE_ELEVATED_THRESHOLD) return 'elevated';
  if (z >= 0) return 'normal';
  return 'low';
}

export const ZSCORE_BUCKET_COLORS: Record<ZScoreBucket, string> = {
  high: ZSCORE_HIGH_COLOR,
  elevated: ZSCORE_ELEVATED_COLOR,
  normal: ZSCORE_NORMAL_COLOR,
  low: ZSCORE_LOW_COLOR,
};

export function zscoreEdgeStyle(z: number): { base: string; alpha: string; anomalous: boolean } {
  if (z > ZSCORE_HIGH_THRESHOLD) return { base: ZSCORE_HIGH_COLOR, alpha: ZSCORE_HIGH_ALPHA, anomalous: true };
  if (z > ZSCORE_ELEVATED_THRESHOLD) return { base: ZSCORE_ELEVATED_COLOR, alpha: ZSCORE_ELEVATED_ALPHA, anomalous: true };
  if (z >= 0) return { base: ZSCORE_NORMAL_COLOR, alpha: ZSCORE_NORMAL_ALPHA, anomalous: false };
  return { base: ZSCORE_LOW_COLOR, alpha: ZSCORE_LOW_ALPHA, anomalous: false };
}

export function computeEdgeColor(
  edge: Pick<
    EdgeAttributes,
    'mentionBase' | 'communityBase' | 'platformBase' | 'interCommunity' | 'reciprocal' | 'zScore' | 'scopeFlow'
  >,
  colorMode: GraphColorMode,
  interClusterMode: InterClusterMode,
  zscoreColoring = false,
  volumeSized = false,
  // top-volume treatment (brightest hue, opaque, no cross-cluster mute) — the
  // caller gates it on the "Thickness by interactions" switch
  topVolume = false,
): string {
  // thick (volume-sized) edges need far less transparency — see VOLUME_EDGE_OPACITY_LIFT
  const edgeAlpha = (alphaHex: string) => (volumeSized ? liftAlpha(alphaHex, VOLUME_EDGE_OPACITY_LIFT) : alphaHex);
  const finish = (hue: string, alphaHex: string) =>
    topVolume ? topVolumeEdgeColor(hue) : premultiplyEdgeColor(hue, edgeAlpha(alphaHex));
  if (zscoreColoring) {
    const { base, alpha, anomalous } = zscoreEdgeStyle(edge.zScore);
    // anomalous edges keep their full hue even across clusters — surfacing them is
    // the whole point of this mode; only normal/low edges get the cross-cluster mute
    const treatAsInter = edge.interCommunity && interClusterMode !== 'full' && !anomalous && !topVolume;
    const hue = treatAsInter ? mixWithBackground(base, INTER_CLUSTER_MUTE) : base;
    return finish(hue, alpha);
  }
  if (colorMode === 'scope' || colorMode === 'connection') {
    // 'scope' only echoes the actors' scope: internal traffic keeps the in-scope
    // hue, anything touching an external actor stays background gray.
    // 'connection' colours every flow relative to the group except out ↔ out
    // chatter (see CONNECTION_EDGE_STYLES).
    const { base, alpha, reciprocalAlpha } =
      colorMode === 'scope'
        ? SCOPE_EDGE_STYLES[edge.scopeFlow === 'internal' ? 'internal' : 'external']
        : CONNECTION_EDGE_STYLES[edge.scopeFlow];
    // the surfaced flows are what 'connection' is for — like anomalous z-score
    // edges they're exempt from the cross-cluster mute
    const treatAsInter =
      edge.interCommunity &&
      interClusterMode !== 'full' &&
      !topVolume &&
      !(colorMode === 'connection' && isConnectionSurfacedFlow(edge.scopeFlow));
    const hue = treatAsInter ? mixWithBackground(base, INTER_CLUSTER_MUTE) : base;
    return finish(hue, edge.reciprocal ? reciprocalAlpha : alpha);
  }
  const base =
    colorMode === 'mono'
      ? MONO_BASE
      : colorMode === 'community'
        ? edge.communityBase
        : colorMode === 'platform'
          ? edge.platformBase
          : edge.mentionBase;
  const treatAsInter = edge.interCommunity && interClusterMode !== 'full' && !topVolume;
  let alpha: string;
  if (treatAsInter) {
    alpha = edge.reciprocal ? INTER_COMMUNITY_RECIPROCAL_EDGE_ALPHA : INTER_COMMUNITY_EDGE_ALPHA;
  } else if (colorMode === 'mention') {
    alpha = edge.reciprocal ? RECIPROCAL_EDGE_ALPHA : EDGE_ALPHA;
  } else {
    // community, mono, and platform modes share hues across many edges, so they
    // stay more transparent
    alpha = edge.reciprocal ? COMMUNITY_RECIPROCAL_EDGE_ALPHA : COMMUNITY_EDGE_ALPHA;
  }
  const hue = treatAsInter ? mixWithBackground(base, INTER_CLUSTER_MUTE) : base;
  return finish(hue, alpha);
}

export function computeNodeColor(
  node: Pick<NodeAttributes, 'community' | 'inEvent' | 'isDust' | 'platformBase'>,
  colorMode: GraphColorMode,
): string {
  // scope mode encodes in/out-of-scope in the hue itself, so external nodes keep
  // full opacity — the transparency trick is only needed when hue carries community
  if (colorMode === 'scope') {
    const color = node.inEvent ? SCOPE_IN_NODE_COLOR : SCOPE_OUT_NODE_COLOR;
    return node.isDust ? mixWithBackground(color, DUST_MUTE) : color;
  }
  // 'connection' keeps every node neutral so the vivid in/out edge pair is the
  // only color signal on the canvas
  if (colorMode === 'connection') {
    return node.isDust ? mixWithBackground(SCOPE_OUT_NODE_COLOR, DUST_MUTE) : SCOPE_OUT_NODE_COLOR;
  }
  const base =
    colorMode === 'mono'
      ? MONO_BASE
      : colorMode === 'platform'
        ? node.platformBase
        : COMMUNITY_PALETTE[node.community % COMMUNITY_PALETTE.length];
  const color = node.inEvent ? base : premultiplyColor(base, EXTERNAL_ALPHA);
  return node.isDust ? mixWithBackground(color, DUST_MUTE) : color;
}

export const LABEL_TEXT_COLOR = '#E5E9F5';
export const LABEL_BACKGROUND_COLOR = 'rgba(14, 14, 16, 0.8)';

// Spotlight-dimmed nodes are monochromatic gray, not near-background: the point of
// a category spotlight is "only the selection has color" — the rest of the network
// should stay readable as colorless context, not vanish
export const DIMMED_NODE_COLOR = '#4A505E';

// Category spotlight repaint (designer spec 2026-08-31): while a category is
// selected in the Actors tab the graph dims and the category's members are
// repainted in ONE signal hue — red for the dangerous categories (high-risk,
// inauthentic coordination), blue for every other category. The legend mirrors
// the same hue next to the category name. Community and z-score spotlights keep
// members' own colors — only categories repaint.
export const CATEGORY_DANGER_HIGHLIGHT_COLOR = '#E25B5B';
export const CATEGORY_HIGHLIGHT_COLOR = '#7FA6E0';

// The signal hue for a category spotlight; null for non-category highlights
// (community / z-score band), which keep members' stored colors.
export function categoryHighlightColor(highlight: GraphHighlight): string | null {
  if (highlight.type === 'role') {
    return highlight.value === 'Inauthentic Coordination'
      ? CATEGORY_DANGER_HIGHLIGHT_COLOR
      : CATEGORY_HIGHLIGHT_COLOR;
  }
  if (highlight.type === 'flag') {
    return highlight.value === 'highRisk' ? CATEGORY_DANGER_HIGHLIGHT_COLOR : CATEGORY_HIGHLIGHT_COLOR;
  }
  return null;
}

// Hover/selection context dim: nodes and edges outside the active node's
// neighborhood (and edges outside a spotlighted group) keep their OWN hue,
// muted this far toward the background — so the user can still tell which
// communities surround the selection and how they connect. RGB mute, never
// alpha (WebGL premultiply, see notes above). The old treatment — non-neighbor
// nodes hidden outright, edges flattened to a near-background gray — read as
// "the rest of the graph vanished" and was rolled back on user feedback.
export const CONTEXT_NODE_MUTE = 0.6;
export const CONTEXT_EDGE_MUTE = 0.8;

export const CONNECTOR_RING_COLOR = '#E3C567CC';
export const CONNECTOR_RING_DIMMED_COLOR = '#E3C56715';

export const DUST_MUTE = 0.45;
// Canvas background #0e0e10 (keep in sync with .graphWrapper in the scss) — dust
// colors are blended toward it (WebGL premultiplied blending means lowering alpha
// alone wouldn't dim the RGB)
export const BACKGROUND_RGB = [14, 14, 16];

export function mixWithBackground(color: string, t: number): string {
  const alphaSuffix = color.length > 7 ? color.slice(7) : '';
  const mixed = [0, 1, 2].map((i) => {
    const channel = parseInt(color.slice(1 + i * 2, 3 + i * 2), 16);
    return Math.round(channel + (BACKGROUND_RGB[i] - channel) * t)
      .toString(16)
      .padStart(2, '0');
  });
  return `#${mixed.join('')}${alphaSuffix}`;
}

// Memoised mixWithBackground for render-time reducers, which run once per node
// and edge on every refresh (every hover change and timeline tick): the stored
// colors come from a few small palettes, so the cache stays tiny while the
// per-item string parsing disappears. Keyed by mute factor, then color.
const mutedColorCache = new Map<number, Map<string, string>>();

export function mixWithBackgroundCached(color: string, t: number): string {
  let byColor = mutedColorCache.get(t);
  if (!byColor) {
    byColor = new Map();
    mutedColorCache.set(t, byColor);
  }
  let mixed = byColor.get(color);
  if (mixed === undefined) {
    mixed = mixWithBackground(color, t);
    byColor.set(color, mixed);
  }
  return mixed;
}

